import { useMutation } from '@tanstack/react-query'
import { useRouter } from 'expo-router'
import type React from 'react'
import { useCallback } from 'react'
import { Platform, ScrollView, StyleSheet, View } from 'react-native'
import { Card, HelperText, Icon, Text, useTheme } from 'react-native-paper'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useAvoidSoftInputForScreen } from '@/hooks/useAvoidSoftInputForScreen'
import { useSlug } from '@/hooks/useSlug'
import { errorToString } from '@/lib/errors'
import { notify } from '@/lib/notice'
import { PB_SERVER_ADDR, pb } from '@/lib/pocketbase'
import { Button } from '@/ui/Button'
import { type Control, TextInput, useForm, z, zodResolver } from '@/ui/form'
import { ButtonLink, Link } from '@/ui/Link'

const signUpSchema = z
    .object({
        name: z.string().min(1, 'Name is required'),
        email: z.string().email('Please enter a valid email address').min(1, 'Email is required'),
        password: z.string().min(8, 'Password must be at least 8 characters long'),
        confirmPassword: z.string().min(1, 'Please confirm your password'),
        orgName: z.string().min(1, 'Organization name is required'),
        orgSlug: z
            .string()
            .min(1, 'Organization slug is required')
            .regex(
                /^[a-z0-9-]+$/,
                'Organization slug can only contain lowercase letters, numbers, and hyphens'
            ),
    })
    .refine(data => data.password === data.confirmPassword, {
        message: 'Passwords do not match',
        path: ['confirmPassword'],
    })

type SignUpFormData = z.infer<typeof signUpSchema>

const useSignUpSubmission = () => {
    const router = useRouter()

    const mutation = useMutation({
        mutationFn: async (data: SignUpFormData) => {
            const response = await fetch(`${PB_SERVER_ADDR}/api/signup`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            })

            if (!response.ok) {
                const errorData = await response
                    .json()
                    .catch(() => ({ message: 'An unexpected error occurred' }))
                throw new Error(errorData.message || 'Sign up failed')
            }

            return response.json()
        },

        onSuccess: result => {
            if (result.token && result.user) {
                pb.authStore.save(result.token, result.user)
            }

            notify.success('Account created successfully!')

            if (result.org?.slug) {
                router.replace(`/${result.org.slug}`)
            } else {
                router.replace('/')
            }
        },
        onError: error => {
            const message = errorToString(error)
            notify.error(message)
        },
    })

    const onSubmit = useCallback(
        (data: SignUpFormData) => {
            mutation.mutate(data)
        },
        [mutation]
    )

    return {
        onSubmit,
        isLoading: mutation.isPending,
        submitError: mutation.error?.message || null,
    }
}

interface SignUpFormProps {
    control: Control<SignUpFormData>
    onSubmit: () => void
    isLoading: boolean
    isValid: boolean
    submitError: string | null
}

const SignUpForm: React.FC<SignUpFormProps> = ({
    control,
    onSubmit,
    isLoading,
    isValid,
    submitError,
}) => (
    <View style={styles.form}>
        <TextInput
            name="name"
            control={control}
            label="Full Name"
            placeholder="Enter your full name"
            autoCapitalize="words"
            autoComplete="name"
        />

        <TextInput
            name="email"
            control={control}
            label="Email Address"
            placeholder="Enter your email"
            keyboardType="email-address"
            autoCapitalize="none"
            autoComplete="email"
        />

        <TextInput
            name="orgName"
            control={control}
            label="Organization Name"
            placeholder="Enter your organization name"
            autoCapitalize="words"
            autoComplete="organization"
        />

        <View style={styles.inputWithHint}>
            <TextInput
                name="orgSlug"
                control={control}
                label="Organization Slug"
                placeholder="your-org-slug"
                autoCapitalize="none"
            />
            <HelperText type="info" style={styles.hintText}>
                This will be used in your organization's URL (e.g., yourapp.com/your-org-slug)
            </HelperText>
        </View>

        <TextInput
            name="password"
            control={control}
            label="Password"
            placeholder="Create a password"
            secureTextEntry
            autoComplete="new-password"
        />

        <TextInput
            name="confirmPassword"
            control={control}
            label="Confirm Password"
            placeholder="Confirm your password"
            secureTextEntry
            autoComplete="new-password"
        />

        {submitError ? (
            <HelperText type="error" style={styles.submitError}>
                {submitError}
            </HelperText>
        ) : null}

        <Button
            title="Create Account"
            mode="contained"
            onPress={onSubmit}
            disabled={isLoading || !isValid}
            loading={isLoading}
            loadingTitle="Creating Account..."
            style={styles.button}
        />
    </View>
)

export default function SignUpPage() {
    useAvoidSoftInputForScreen()
    const { onSubmit, isLoading, submitError } = useSignUpSubmission()
    const theme = useTheme()

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { isValid },
    } = useForm<SignUpFormData>({
        resolver: zodResolver(signUpSchema),
        mode: 'onBlur',
        defaultValues: {
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
            orgName: '',
            orgSlug: '',
        },
    })

    useSlug<SignUpFormData>({ watch, setValue }, 'orgName', 'orgSlug')

    const ContentWrapper = Platform.OS === 'ios' ? SafeAreaView : View

    return (
        <ContentWrapper
            style={[styles.safeContainer, { backgroundColor: theme.colors.background }]}
        >
            <ScrollView style={styles.scrollView} contentContainerStyle={styles.container}>
                <Card
                    style={[
                        styles.warningCard,
                        { backgroundColor: theme.colors.secondaryContainer },
                    ]}
                >
                    <Card.Content style={styles.warningContent}>
                        <View style={styles.warningHeader}>
                            <Icon
                                source="information-outline"
                                size={24}
                                color={theme.colors.onSecondaryContainer}
                            />
                            <Text
                                variant="titleMedium"
                                style={[
                                    styles.warningTitle,
                                    { color: theme.colors.onSecondaryContainer },
                                ]}
                            >
                                Setting Up a New Organization?
                            </Text>
                        </View>
                        <Text
                            variant="bodyMedium"
                            style={{ color: theme.colors.onSecondaryContainer }}
                        >
                            This page is for creating a new organization. If your employer invited
                            you to join theirs, check your email for an invite link instead.
                        </Text>
                        <ButtonLink
                            href="/"
                            title="I have an invite link"
                            mode="outlined"
                            style={styles.warningButton}
                        />
                    </Card.Content>
                </Card>

                <View style={styles.header}>
                    <Text variant="headlineLarge">Create Organization</Text>
                    <Text variant="bodyMedium" style={styles.subtitle}>
                        Set up your business on JobTime
                    </Text>
                </View>

                <SignUpForm
                    control={control}
                    onSubmit={handleSubmit(onSubmit)}
                    isLoading={isLoading}
                    isValid={isValid}
                    submitError={submitError}
                />

                <View style={styles.footer}>
                    <View style={styles.linkRow}>
                        <Text variant="bodyMedium">Already have an account? </Text>
                        <Link href="/auth/sign-in">
                            <Text variant="bodyMedium" style={{ color: theme.colors.primary }}>
                                Sign In
                            </Text>
                        </Link>
                    </View>
                </View>
            </ScrollView>
        </ContentWrapper>
    )
}

const styles = StyleSheet.create({
    safeContainer: {
        flex: 1,
    },
    scrollView: {
        flex: 1,
    },
    container: {
        padding: 16,
        maxWidth: 400,
        width: '100%',
        alignSelf: 'center',
        minHeight: '100%',
        gap: 16,
    },
    warningCard: {
        marginBottom: 8,
    },
    warningContent: {
        gap: 12,
    },
    warningHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    warningTitle: {
        fontWeight: '600',
    },
    warningButton: {
        marginTop: 4,
    },
    header: {
        gap: 8,
    },
    subtitle: {
        opacity: 0.7,
    },
    form: {
        gap: 16,
    },
    inputWithHint: {
        marginBottom: 8,
    },
    hintText: {
        fontSize: 12,
        marginTop: 4,
    },
    submitError: {
        marginTop: 8,
    },
    button: {
        marginTop: 16,
    },
    footer: {
        marginTop: 24,
    },
    linkRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
})
